import React, { useState } from 'react';
import {
  MessageCircle, Phone, CheckCircle2, XCircle, Loader2,
  Clock, Wallet, Copy, Check, ListChecks,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  useGetAdminPendingOrdersQuery,
  useAdminMarkContactedMutation,
  useAdminConfirmOrderMutation,
  useAdminRejectOrderMutation,
  useGetAdminPendingPayoutsQuery,
  useAdminConfirmPayoutMutation,
  useAdminRejectPayoutMutation,
} from '../../api/apiSlice';
import Loading from '../../components/common/Loading';

// В финансовых контекстах 0 ₸ — это реальная сумма, не "бесплатно".
const fmt = (n) => `${(Number(n) || 0).toLocaleString('ru-RU')} ₸`;

function CopyButton({ value }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard.writeText(value).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      }}
      className="p-1 rounded text-gray-400 hover:text-indigo-600 transition"
      title="Скопировать"
    >
      {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

function OrderCard({ order }) {
  const [markContacted] = useAdminMarkContactedMutation();
  const [confirmOrder, { isLoading: confirming }] = useAdminConfirmOrderMutation();
  const [rejectOrder, { isLoading: rejecting }] = useAdminRejectOrderMutation();
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const whatsappUrl = `https://wa.me/${order.studentPhone?.replace(/\D/g, '')}?text=${encodeURIComponent(
    `Здравствуйте! Это Сайат из Uaqyt. Вы оставили заявку на курс "${order.course?.title || ''}" на ${fmt(order.amount)}. Для оплаты переведите эту сумму через Kaspi на номер +7 ___, в комментарии укажите email ${order.user?.email || ''}.`
  )}`;

  const handleConfirm = async () => {
    if (!window.confirm('Подтвердить получение оплаты? Курс откроется студенту.')) return;
    try {
      await confirmOrder(order.id).unwrap();
      toast.success('Оплата подтверждена, курс открыт');
    } catch (err) {
      toast.error(err?.data?.message || 'Ошибка');
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Укажите причину');
      return;
    }
    try {
      await rejectOrder({ orderId: order.id, reason: rejectReason.trim() }).unwrap();
      toast.success('Заявка отклонена');
      setShowReject(false);
    } catch (err) {
      toast.error(err?.data?.message || 'Ошибка');
    }
  };

  const isPending = order.status === 'pending_contact';

  return (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="text-base font-semibold text-gray-900 dark:text-white truncate">
            {order.user?.name || 'Студент'}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {order.user?.email} · {new Date(order.createdAt).toLocaleString('ru-RU', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' })}
          </div>
        </div>
        <span className={`text-xs px-2.5 py-1 rounded-full font-medium shrink-0 ${
          isPending
            ? 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300'
            : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'
        }`}>
          {isPending ? 'Ждёт связи' : 'Подтверждено'}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm mb-4">
        <div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Курс</div>
          <div className="font-medium text-gray-900 dark:text-white truncate">{order.course?.title}</div>
          <div className="text-xs text-gray-500 mt-0.5">Инструктор: {order.course?.instructor?.name}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Сумма</div>
          <div className="font-semibold text-gray-900 dark:text-white tabular-nums">{fmt(order.amount)}</div>
          <div className="text-xs text-gray-500 mt-0.5">
            Инструктору: {fmt(order.instructorShare)} · Платформа: {fmt(order.platformFee)}
          </div>
        </div>
        <div className="sm:col-span-2">
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Контакт</div>
          <div className="flex items-center gap-2">
            <code className="text-sm font-mono bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
              {order.studentPhone}
            </code>
            <CopyButton value={order.studentPhone} />
            {order.studentHasWhatsapp && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 flex items-center gap-1">
                <MessageCircle className="h-3 w-3" /> WhatsApp
              </span>
            )}
          </div>
        </div>
        {order.studentNote && (
          <div className="sm:col-span-2">
            <div className="text-xs text-gray-500 dark:text-gray-400">Комментарий</div>
            <div className="text-sm text-gray-700 dark:text-gray-300 italic">{order.studentNote}</div>
          </div>
        )}
      </div>

      {isPending && (
        <>
          <div className="flex flex-wrap gap-2 mb-2">
            {order.studentHasWhatsapp && (
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => markContacted(order.id)}
                className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 text-sm font-medium"
              >
                <MessageCircle className="h-4 w-4" /> WhatsApp
              </a>
            )}
            <a
              href={`tel:${order.studentPhone}`}
              onClick={() => markContacted(order.id)}
              className="inline-flex items-center gap-2 rounded-xl border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 px-4 py-2 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              <Phone className="h-4 w-4" /> Позвонить
            </a>
          </div>

          <div className="flex gap-2 pt-3 border-t border-gray-100 dark:border-gray-800">
            <button
              type="button"
              onClick={handleConfirm}
              disabled={confirming}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2.5 text-sm font-semibold disabled:opacity-50"
            >
              {confirming ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              Подтвердить оплату
            </button>
            <button
              type="button"
              onClick={() => setShowReject(true)}
              className="rounded-xl border border-red-300 dark:border-red-900/50 text-red-600 dark:text-red-400 px-4 py-2.5 text-sm font-medium hover:bg-red-50 dark:hover:bg-red-950/20"
            >
              <XCircle className="h-4 w-4" />
            </button>
          </div>

          {showReject && (
            <div className="mt-3 rounded-xl bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/40 p-3 space-y-2">
              <input
                type="text"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Причина отклонения"
                className="w-full rounded-lg border border-red-300 dark:border-red-800 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
              />
              <div className="flex gap-2">
                <button onClick={handleReject} disabled={rejecting} className="flex-1 rounded-lg bg-red-600 hover:bg-red-700 text-white py-2 text-sm font-medium disabled:opacity-50">Отклонить</button>
                <button onClick={() => setShowReject(false)} className="rounded-lg border border-red-300 dark:border-red-700 px-3 py-2 text-sm text-gray-600">Отмена</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function PayoutCard({ payout }) {
  const [confirmPayout, { isLoading: confirming }] = useAdminConfirmPayoutMutation();
  const [rejectPayout, { isLoading: rejecting }] = useAdminRejectPayoutMutation();
  const [showConfirm, setShowConfirm] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [rejectReason, setRejectReason] = useState('');

  const handleConfirm = async () => {
    try {
      await confirmPayout({ payoutId: payout.id, kaspiReference: reference.trim() || undefined, note: note.trim() || undefined }).unwrap();
      toast.success('Выплата зафиксирована');
      setShowConfirm(false);
    } catch (err) {
      toast.error(err?.data?.message || 'Ошибка');
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Укажите причину');
      return;
    }
    try {
      await rejectPayout({ payoutId: payout.id, reason: rejectReason.trim() }).unwrap();
      toast.success('Заявка отклонена');
      setShowReject(false);
    } catch (err) {
      toast.error(err?.data?.message || 'Ошибка');
    }
  };

  return (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="text-base font-semibold text-gray-900 dark:text-white">
            {payout.instructor?.name || 'Инструктор'}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {payout.instructor?.email} · {new Date(payout.requestedAt).toLocaleString('ru-RU')}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-xl font-bold text-gray-900 dark:text-white tabular-nums">
            {fmt(payout.amount)}
          </div>
          <div className="text-xs text-gray-500">{payout.orders?.length || 0} заказов</div>
        </div>
      </div>

      <div className="rounded-xl bg-indigo-50 dark:bg-indigo-950/30 px-3 py-2 mb-3 flex items-center gap-2">
        <Phone className="h-4 w-4 text-indigo-600" />
        <code className="text-sm font-mono text-indigo-900 dark:text-indigo-200">{payout.kaspiPhone}</code>
        <CopyButton value={payout.kaspiPhone} />
      </div>

      {payout.notes && (
        <div className="text-sm text-gray-600 dark:text-gray-300 italic mb-3">
          "{payout.notes}"
        </div>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setShowConfirm(true)}
          className="flex-1 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2.5 text-sm font-semibold flex items-center justify-center gap-2"
        >
          <CheckCircle2 className="h-4 w-4" /> Отметить выплаченным
        </button>
        <button
          type="button"
          onClick={() => setShowReject(true)}
          className="rounded-xl border border-red-300 dark:border-red-900/50 text-red-600 dark:text-red-400 px-4 py-2.5 text-sm font-medium"
        >
          <XCircle className="h-4 w-4" />
        </button>
      </div>

      {showConfirm && (
        <div className="mt-3 rounded-xl bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 p-3 space-y-2">
          <input type="text" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Kaspi reference (опц.)" className="w-full rounded-lg border border-emerald-300 dark:border-emerald-800 bg-white dark:bg-gray-900 px-3 py-2 text-sm" />
          <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Примечание (опц.)" className="w-full rounded-lg border border-emerald-300 dark:border-emerald-800 bg-white dark:bg-gray-900 px-3 py-2 text-sm" />
          <div className="flex gap-2">
            <button onClick={handleConfirm} disabled={confirming} className="flex-1 rounded-lg bg-emerald-600 text-white py-2 text-sm font-medium disabled:opacity-50">Подтвердить</button>
            <button onClick={() => setShowConfirm(false)} className="rounded-lg border border-emerald-300 px-3 py-2 text-sm">Отмена</button>
          </div>
        </div>
      )}

      {showReject && (
        <div className="mt-3 rounded-xl bg-red-50 dark:bg-red-950/20 border border-red-200 p-3 space-y-2">
          <input type="text" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Причина отклонения" className="w-full rounded-lg border border-red-300 bg-white dark:bg-gray-900 px-3 py-2 text-sm" />
          <div className="flex gap-2">
            <button onClick={handleReject} disabled={rejecting} className="flex-1 rounded-lg bg-red-600 text-white py-2 text-sm font-medium disabled:opacity-50">Отклонить</button>
            <button onClick={() => setShowReject(false)} className="rounded-lg border border-red-300 px-3 py-2 text-sm">Отмена</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminP2pPage() {
  const [tab, setTab] = useState('orders');
  const { data: ordersData, isLoading: loadingOrders } = useGetAdminPendingOrdersQuery(undefined, { pollingInterval: 30000 });
  const { data: payoutsData, isLoading: loadingPayouts } = useGetAdminPendingPayoutsQuery(undefined, { pollingInterval: 30000 });

  const orders = ordersData?.orders || [];
  const payouts = payoutsData?.payouts || [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Платежи</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Очередь заявок от студентов и выплат инструкторам
        </p>
      </div>

      <div className="flex gap-2 rounded-2xl bg-gray-100 dark:bg-gray-800 p-1.5">
        <button
          type="button"
          onClick={() => setTab('orders')}
          className={`flex-1 flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition ${
            tab === 'orders' ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-600 dark:text-gray-400'
          }`}
        >
          <ListChecks className="h-4 w-4" />
          Заявки от студентов
          {orders.length > 0 && (
            <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-amber-500 text-white">
              {orders.length}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={() => setTab('payouts')}
          className={`flex-1 flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition ${
            tab === 'payouts' ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-600 dark:text-gray-400'
          }`}
        >
          <Wallet className="h-4 w-4" />
          Выплаты инструкторам
          {payouts.length > 0 && (
            <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-indigo-500 text-white">
              {payouts.length}
            </span>
          )}
        </button>
      </div>

      {tab === 'orders' ? (
        loadingOrders ? (
          <Loading variant="card" label="Загрузка заявок" />
        ) : orders.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-300 dark:border-gray-700 p-12 text-center text-gray-500 dark:text-gray-400">
            <Clock className="h-10 w-10 mx-auto mb-3 opacity-40" />
            <p>Заявок на оплату нет</p>
            <p className="text-xs mt-1">Появятся когда студент нажмёт «Купить»</p>
          </div>
        ) : (
          <div className="space-y-3">{orders.map((o) => <OrderCard key={o.id} order={o} />)}</div>
        )
      ) : loadingPayouts ? (
        <Loading variant="card" label="Загрузка выплат" />
      ) : payouts.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-300 dark:border-gray-700 p-12 text-center text-gray-500 dark:text-gray-400">
          <Wallet className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <p>Заявок на выплату нет</p>
        </div>
      ) : (
        <div className="space-y-3">{payouts.map((p) => <PayoutCard key={p.id} payout={p} />)}</div>
      )}
    </div>
  );
}
